import { useState, useEffect } from 'react';
import { PerformanceGroup } from '@/types/performance-analyze';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

interface GroupFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group?: PerformanceGroup | null;
  onSubmit: (data: Partial<PerformanceGroup>) => void;
}

interface FormData {
  symbol: string;
  prefix: string;
  strategy: 'TR' | 'SW';
  size_group: 'Large' | 'Small';
  tp_values: string;
}

export const GroupFormDialog = ({ 
  open, 
  onOpenChange, 
  group, 
  onSubmit 
}: GroupFormDialogProps) => {
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      symbol: '',
      prefix: '',
      strategy: 'TR',
      size_group: 'Large',
      tp_values: ''
    }
  });

  useEffect(() => {
    if (group) {
      reset({
        symbol: group.symbol,
        prefix: group.prefix || '',
        strategy: group.strategy,
        size_group: group.size_group,
        tp_values: group.tp_values.join(', ')
      });
    } else {
      reset({
        symbol: '',
        prefix: '',
        strategy: 'TR',
        size_group: 'Large',
        tp_values: ''
      });
    }
  }, [group, reset]);

  const handleFormSubmit = (data: FormData) => {
    // Parse and clean symbol
    let cleanSymbol = data.symbol.trim();
    let cleanPrefix = data.prefix?.trim() || null;
    
    // Check if symbol starts with emoji/special char
    const firstChar = cleanSymbol[0];
    if (firstChar && !/[a-zA-Z0-9]/.test(firstChar)) {
      // Has prefix in symbol field
      cleanPrefix = firstChar;
      cleanSymbol = cleanSymbol.slice(1).trim();
      
      toast.info(`Detected prefix "${cleanPrefix}" in symbol. Moving to PREFIX field.`);
    }
    
    // Remove any spaces or special chars from symbol
    cleanSymbol = cleanSymbol.replace(/[^a-zA-Z0-9.]/g, '');
    
    if (!cleanSymbol) {
      toast.error('Symbol cannot be empty');
      return;
    }

    // Clean prefix (keep only first character, remove spaces)
    if (cleanPrefix && cleanPrefix.length > 0) {
      cleanPrefix = cleanPrefix[0];
    }

    // Validate TP values
    const tpValues = data.tp_values
      .split(',')
      .map(v => v.trim())
      .filter(v => v)
      .map(v => parseFloat(v));

    if (tpValues.length === 0) {
      toast.error('Please enter at least one TP value');
      return;
    }

    if (tpValues.some(v => isNaN(v))) {
      toast.error('TP values must be valid numbers');
      return;
    }

    onSubmit({
      ...(group ? { id: group.id } : {}),
      symbol: cleanSymbol,
      prefix: cleanPrefix,
      strategy: data.strategy,
      size_group: data.size_group,
      tp_values: tpValues
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{group ? 'Edit Group' : 'Create New Group'}</DialogTitle>
          <DialogDescription>
            Define performance analysis group parameters
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="symbol">Symbol *</Label>
            <Input
              id="symbol"
              placeholder="e.g., XAUUSD, UNIUSDT.P (without prefix)"
              {...register('symbol', { required: true })}
            />
            <p className="text-xs text-muted-foreground">
              Enter symbol name only. Prefix will be auto-detected if included.
            </p>
            {errors.symbol && (
              <p className="text-sm text-destructive">Symbol is required</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="prefix">PREFIX (Emoji/Icon)</Label>
            <Input
              id="prefix"
              placeholder="e.g., ❗, 🔔, or leave empty"
              maxLength={1}
              {...register('prefix')}
            />
            <p className="text-xs text-muted-foreground">
              Optional single character/emoji to identify variations.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Strategy *</Label>
            <RadioGroup
              value={watch('strategy')}
              onValueChange={(value) => setValue('strategy', value as 'TR' | 'SW')}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="TR" id="tr" />
                <Label htmlFor="tr">TR (Trend)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="SW" id="sw" />
                <Label htmlFor="sw">SW (Sideways)</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Size Group *</Label>
            <RadioGroup
              value={watch('size_group')}
              onValueChange={(value) => setValue('size_group', value as 'Large' | 'Small')}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Large" id="large" />
                <Label htmlFor="large">Large</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Small" id="small" />
                <Label htmlFor="small">Small</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tp_values">TP Values *</Label>
            <Input
              id="tp_values"
              placeholder="e.g., 0.4, 0.35"
              {...register('tp_values', { required: true })}
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated list of TP values (e.g., 0.4, 0.35, 0.25)
            </p>
            {errors.tp_values && (
              <p className="text-sm text-destructive">TP values are required</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {group ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
